import {
	type SDK_Blank,
	type SDK_Filter,
	strings,
} from "@quickpoint.me/shared";
import { type ReactElement, memo } from "react";
import styled, { type DefaultTheme, ThemeProvider } from "styled-components";
import { FilterName, FilterSelector } from "../../state/filter";
import {
	PageElementsFamily,
	PageSizeAtom,
	createLocation,
} from "../../state/page";
import { AutoSpaceAtom, AutoSpaceFilterSetting } from "../../state/ui";

import { useRecoilValue } from "recoil";
import type { FilterInterface } from "../../@types/viewerati";
import type { ElementProps } from "../../functionalComponents/RenderPage";
import { fontSize } from "../../lib/cssHelpers";

const StyledBlank = styled.div<{ $leading: string | 0 }>`
  min-height: ${(props) => props.$leading};
  height: ${(props) => props.$leading};
`;

const blankFilter = (theme: DefaultTheme): SDK_Blank => {
	return theme.blank;
};

// if the user or filter disables auto-spacing it this is reflected in the autoSpaceAtom
const Blank = ({
	atomId,
	pageIndex,
	isAutospaceElement,
}: ElementProps): ReactElement<ElementProps> => {
	const pageSize = useRecoilValue(PageSizeAtom);
	const baseFontSize = pageSize.baseFontSize;
	const userDefinedAutoSpace = useRecoilValue(AutoSpaceAtom);
	const filterDefinedAutoSpace = useRecoilValue(AutoSpaceFilterSetting);
	const key = createLocation(pageIndex, atomId);
	const elementData = useRecoilValue(PageElementsFamily(key)).data;
	const filterName = useRecoilValue(FilterName);
	const filter = useRecoilValue(FilterSelector(filterName));
	const previousElementKey = createLocation(
		pageIndex,
		atomId > 0 ? atomId - 1 : 0,
	);
	const previousElement = useRecoilValue(
		PageElementsFamily(previousElementKey),
	).data;

	let blankLeading: string | 0 = 0;

	if (isAutospaceElement) {
		if (atomId === 0) return <></>; // first element doesn't have autospace before it
		if (!userDefinedAutoSpace) return <></>;
		if (!filterDefinedAutoSpace) return <></>;
		if (previousElement.lastElement) return <></>;

		const filterType = previousElement.type.slice(0, -1) as keyof SDK_Filter; // remove the trailing colon
		const previousElementFilter = filter[filterType] as FilterInterface;

		if (previousElementFilter) {
			blankLeading = fontSize(
				previousElementFilter,
				baseFontSize,
				filter.blank.leading,
			);
		} else {
			blankLeading = `${baseFontSize}px`; // default multiplier is 1
		}
	} else {
		// Handle explicit blank:
		if (elementData.type === "blank:") {
			let multiplier = 1; // Default for a blank line (newline)
			if (elementData.content && typeof elementData.content === "string") {
				const parsedMultiplier = Number.parseInt(elementData.content, 10);
				if (!Number.isNaN(parsedMultiplier)) {
					multiplier = parsedMultiplier;
				}
			}

			blankLeading = fontSize(filter.blank, baseFontSize, multiplier);
		}
	}

	if (!blankLeading) {
		return <></>;
	}

	return (
		<ThemeProvider theme={blankFilter}>
			<StyledBlank
				$leading={blankLeading}
				aria-hidden={true}
				className={`${strings.css.pageElement}  blank ${isAutospaceElement ? "autospace" : ""}`}
				// data-atom-id={atomId}
			/>
		</ThemeProvider>
	);
};

export default memo(Blank);
